import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import WebSocketService from "../components/WebSockets/WebSocketService";

// Subscription key used with WebSocketService.subscribeToTopic /
// unsubscribeFromTopic. Kept distinct per view so an individual-view
// subscription and a team-view subscription can never clobber each other's
// entry in WebSocketService's internal `subscriptions` / `pendingSubscriptions`
// maps if both hooks were ever mounted at once.
const SUBSCRIPTION_KEY = {
  individual: "operations",
  team: "operations-team",
};

/**
 * Connects to the WebSocket server and subscribes to operation-stage update
 * notifications for either a single employee or the whole team, mirroring
 * the pattern already used for enquiry updates in Enquiries.jsx /
 * TeamEnquiry.jsx.
 *
 * Wire `onUpdate` to whatever re-fetches
 * GET /api/operation-stage-histories/details (or a single-record refresh)
 * in the calling page — this hook only owns the socket connection,
 * subscription, and debouncing; it doesn't know how your data is fetched.
 *
 * @param {Object} params
 * @param {string|number} params.tenantId
 * @param {string|number} params.empId - required for individual view; not
 *   used (but harmless if present) for team view, since the team topic is
 *   tenant-wide.
 * @param {boolean} [params.isTeamView=false] - subscribes to the tenant-wide
 *   operations topic instead of the per-employee one.
 * @param {(message: any) => void} params.onUpdate - called (after debounce)
 *   with the raw message payload whenever an update notification arrives.
 *   Typically this triggers a refetch.
 * @param {number} [params.debounceMs=1000] - collapses bursts of updates
 *   (e.g. several checklist items completed back-to-back) into a single
 *   onUpdate call.
 * @param {boolean} [params.showToast=true] - shows a toast.info on each
 *   incoming notification, same as the existing enquiry websocket handler.
 * @param {string} [params.toastMessage="Operation updated in real-time"]
 *
 * @returns {{ isWebSocketConnected: boolean }}
 */
export function useOperationsWebSocket({
  tenantId,
  empId,
  isTeamView = false,
  onUpdate,
  debounceMs = 1000,
  showToast = true,
  toastMessage = "Operation updated in real-time",
}) {
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

  // Keep the latest onUpdate in a ref so the effect below doesn't need to
  // re-subscribe every time the caller passes a new inline function —
  // only tenantId/empId/isTeamView changes should tear down and
  // re-establish the subscription.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (!tenantId) {
      console.warn(
        "[useOperationsWebSocket] Skipping connect — tenantId is missing.",
      );
      return;
    }
    if (!isTeamView && !empId) {
      console.warn(
        "[useOperationsWebSocket] Skipping connect — empId is missing " +
          "(required for the individual/non-team topic). Check the shape " +
          "of your user object.",
      );
      return;
    }

    WebSocketService.connect({ tenantId, empId });

    const subscriptionKey = isTeamView
      ? SUBSCRIPTION_KEY.team
      : SUBSCRIPTION_KEY.individual;

    const handleMessage = (message) => {
      console.log("Operation WebSocket Payload:", message);

      if (showToast) {
        toast.info(toastMessage);
      }

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onUpdateRef.current?.(message);
      }, debounceMs);
    };

    if (isTeamView) {
      WebSocketService.subscribeToTeamOperationsUpdates(
        tenantId,
        handleMessage,
      );
    } else {
      WebSocketService.subscribeToOperationsUpdates(
        tenantId,
        empId,
        handleMessage,
      );
    }

    // WebSocketService.connect() resolves asynchronously (SockJS handshake +
    // STOMP CONNECT), so there's no connect-completion callback to hook into
    // directly here. Poll isConnected() the same way the enquiry pages do.
    const connectionCheckInterval = setInterval(() => {
      setIsWebSocketConnected(WebSocketService.isConnected());
    }, 2000);

    return () => {
      clearInterval(connectionCheckInterval);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      WebSocketService.unsubscribeFromTopic(subscriptionKey);
      // Deliberately not calling WebSocketService.disconnect() here — other
      // pages/hooks (e.g. the enquiry websocket) may share the same
      // underlying connection. Only this hook's own subscription is torn
      // down.
    };
  }, [tenantId, empId, isTeamView, debounceMs, showToast, toastMessage]);

  return { isWebSocketConnected };
}

export default useOperationsWebSocket;
